import React, { useMemo, useState } from 'react'
import { Group } from '../../models/conversations';
import { Autocomplete, Avatar, Checkbox, Chip, FormControlLabel, Paper, Radio, RadioGroup, TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import * as selector from "../../selectors/selectors";
import ButtonComponent from '../shared/button/button.component';
import { ToasterTexts } from '../shared/constants';
import { ImportedContact, ImportedContactModelWithErrors, SelectExistingContactAction, SelectGroupAction } from '../../models/importContacts.model';
import { validateForm } from '../shared/form-validation';
import { GroupContactService } from '../../services/GroupContactService';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';
import { Setting } from '../../models/settings-model';
import { SettingsService } from '../../services/SettingsService';
interface IProps {
    NewContacts: ImportedContact[];
    ExistingContacts: ImportedContact[];
    SavedInvalidContacts: ImportedContact[];
    InvalidContacts: ImportedContactModelWithErrors[];
    UploadedFile: File;
    IsChecked: boolean;
    SelectedGroupIds: string[];
    SelectedExistingContactAction?: (action: SelectExistingContactAction) => void
    SelectedGroupAction?: (action: SelectGroupAction) => void;
    UpdateSelectedGroups?: (groups: string[]) => void;
    UpdateIncludeIsGroupCheck?: (isChecked) => void;
}

interface ActionDataModel {
    Value: SelectExistingContactAction | SelectGroupAction,
    PrimaryLabel: string,
    SecondaryLabel: string
}

const existingContactActionData: ActionDataModel[] = [
    { Value: SelectExistingContactAction.Fill_Blank_Fields, PrimaryLabel: "Fill blank fields only", SecondaryLabel: "File fills empty fields — existing Falkon values kept" },
    { Value: SelectExistingContactAction.USE_FILEDATA, PrimaryLabel: "Use file data", SecondaryLabel: "File values overwrite Falkon values where they differ" },
    { Value: SelectExistingContactAction.KEEPEXISTINGFALKON, PrimaryLabel: "Keep Falkon data always", SecondaryLabel: "Existing contacts unchanged — only new contacts added" },
]

const groupActionData: ActionDataModel[] = [
    { Value: SelectGroupAction.BOTH, PrimaryLabel: "Both new and existing", SecondaryLabel: "Everyone from this file" },
    { Value: SelectGroupAction.ONLY_NEW, PrimaryLabel: "New contacts only", SecondaryLabel: "Only first-time additions" },
    { Value: SelectGroupAction.ONLY_EXISTING, PrimaryLabel: "Existing contacts only", SecondaryLabel: "Only already-saved contacts" },
]

const ExcelContactToGroup = (props: IProps) => {
    const Groups: Group[] = useSelector(selector.groups)
    const SettingsData: Setting[] = useSelector(selector.settings)

    const [selectExistingContactAction, setSelectExistingContactAction] = useState<SelectExistingContactAction>(SelectExistingContactAction.Fill_Blank_Fields)
    const [selectGroupAction, setSelectGroupAction] = useState<SelectGroupAction>(SelectGroupAction.BOTH)
    const [selectedGroupIds, setSelectedGroupsds] = useState<string[]>(props.SelectedGroupIds ?? [])
    const [NewGroupName, setNewGroupName] = useState<string>("");
    const [errors, setErrors] = useState({ NewGroupName: "", });
    const [checked, setChecked] = useState<boolean>(props.IsChecked ?? false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onGroupChange = (event: any, values: Group[]) => {
        setSelectedGroupsds(values.map(x => x.ID))
        props.UpdateSelectedGroups(values.map(x => x.ID))
    }

    const editGroup = (name: string, value: string) => {
        validateForm({ [name]: value },
            (errors) => {
                setErrors({ NewGroupName: errors["NewGroupName"] })
            })
        setNewGroupName(value)
    }

    const createGroup = () => {
        const newContacts = props.NewContacts?.length ?? 0;
        const existingContacts = props.ExistingContacts?.length ?? 0;

        let contactCount = 0;

        switch (selectGroupAction) {
            case SelectGroupAction.BOTH:
                contactCount = newContacts + existingContacts;
                break;

            case SelectGroupAction.ONLY_EXISTING:
                contactCount = existingContacts;
                break;

            case SelectGroupAction.ONLY_NEW:
                contactCount = newContacts;
                break;
        }

        if (contactCount > MaxGroupContactCount) {
            setToast(ToasterTexts.GROUP_EDIT_FAILED_MAX_CONTACTS_EXCEEDED.replace("<count>", String(MaxGroupContactCount)), ToasterType.DANGER);
            return;
        }

        setIsLoading(true)
        let data = {
            name: NewGroupName,
            count: 0,
            contacts: []
        };
        GroupContactService.createGroup(data).then((res) => {
            setIsLoading(false);
            setNewGroupName("");
            setToast(ToasterTexts.GROUP_ADD_SUCCESS.replace("<Name>", NewGroupName), ToasterType.SUCCESS);
            setSelectedGroupsds((prev) => [...prev, res.data])
            props.UpdateSelectedGroups([...selectedGroupIds, res.data])

        }).catch((err) => {
            setIsLoading(false);
            setToast(err.response.data.code === "MGC" ? ToasterTexts.GROUP_EDIT_FAILED_MAX_CONTACTS_EXCEEDED.replace("<count>", err.response.data.value) : err.response.data.message, ToasterType.DANGER);
        })
    }

    const onAssignToGroupActionChange = (value: number) => {
        setSelectGroupAction(value);
        props.SelectedGroupAction(value);
        setSelectedGroupsds([]);
        props.UpdateSelectedGroups([])
    }

    const MaxGroupContactCount = useMemo(() => Number(SettingsData.find(x => x.Code === SettingsService.Group_Contacts_Code)?.Value), [])

    const disableOptions = (option: Group) => {
        const newContacts = props.NewContacts?.length ?? 0;
        const existingContacts = props.ExistingContacts?.length ?? 0;

        switch (selectGroupAction) {
            case SelectGroupAction.BOTH:
                return option.ContactsCount + newContacts + existingContacts > MaxGroupContactCount;

            case SelectGroupAction.ONLY_EXISTING:
                return option.ContactsCount + existingContacts > MaxGroupContactCount;

            default:
                return option.ContactsCount + newContacts > MaxGroupContactCount;
        }
    };

    return (
        <div className='w-p-100  br-3 mx-auto'>
            <div className='w-p-95 mx-auto mt-3'>
                <div className='bg-color-secondary w-p-100 br-3 border-primary-divider h5-regular'>
                    <div className='w-p-96 mx-auto d-flex align-center justify-between color-primary-accent'>
                        <div className='py-2'>
                            <span>About to import</span>
                            <span className='ml-2 bg-color-secure-file-active p-1 br-2'>{props.NewContacts?.length} new</span>
                            <span className='ml-2 bg-color-con-hist-outbound-msg-bg p-1 br-2 '>{props.ExistingContacts?.length} existing</span>
                            <span className='bg-color-photosensitivity-warning p-1 br-2 ml-2'>{props.InvalidContacts?.length} invalid + skipped</span>
                        </div>
                        <div className='h4-medium '>
                            {(props.ExistingContacts?.length ?? 0) + (props.NewContacts?.length ?? 0)} Total
                        </div>
                    </div>
                </div>
                <div className='py-2 w-p-100 bg-color-secondary br-3 my-2 border-primary-divider'>
                    <div className='w-p-96 mx-auto'>
                        <div className='d-flex justify-between align-center'>
                            <div className='h3-medium color-primary-accent '>Existing Contacts -  What should we do?</div>
                            {!!props.ExistingContacts?.length && <div className='h5-medium color-menu-list-font-subnav'>{props.ExistingContacts?.length} contacts already in Falkon</div>}
                        </div>
                        <div className='p-2 '>
                            <RadioGroup
                                value={selectExistingContactAction}
                                onChange={(e) => { setSelectExistingContactAction(Number(e.target.value)); props.SelectedExistingContactAction(Number(e.target.value)) }}
                            >
                                <div className='d-flex justify-between'>
                                    {
                                        existingContactActionData.map(x => <div className={`${selectExistingContactAction === x.Value ? "b-around-consent-message-border color-button-text bg-color-consent-message-bg" : "border-primary-divider"}  p-1 w-p-30 br-2`}>
                                            <FormControlLabel
                                                value={x.Value}
                                                control={<Radio color="primary" />}
                                                label={<div className='color-primary-accent'>
                                                    <div className='h4-medium'>{x.PrimaryLabel}</div>
                                                    <div className='h5-regular'>{x.SecondaryLabel}</div>
                                                </div>}
                                            />
                                        </div>)
                                    }
                                </div>
                            </RadioGroup>
                        </div>
                    </div>
                </div>
                <div className=' py-2 w-p-100 bg-color-secondary br-3 border-primary-divider'>
                    <div className='w-p-96 mx-auto'>
                        <div className='d-flex justify-between align-center'>
                            <div className='h3-medium color-primary-accent'>Assign to a group</div>
                            <div className='h5-medium color-menu-list-font-subnav'>Optional — makes them easier to message later</div>
                        </div>
                        <div className=' w-p-100'>
                            <div className='h4-regular '>Which contacts would you like to group?</div>
                            <div className='px-2 pt-2'>
                                <RadioGroup
                                    value={selectGroupAction}
                                    onChange={(e) => { onAssignToGroupActionChange(Number(e.target.value)) }}
                                >
                                    <div className='d-flex justify-between'>
                                        {
                                            groupActionData.map(x => <div className={`${selectGroupAction === x.Value ? "b-around-consent-message-border color-button-text bg-color-consent-message-bg" : "border-primary-divider"}  p-1 w-p-30 br-2 h-fit`}>
                                                <FormControlLabel
                                                    value={x.Value}
                                                    control={<Radio color="primary" />}
                                                    label={<div className='color-primary-accent'>
                                                        <div className='h4-medium'>{x.PrimaryLabel}</div>
                                                        <div className='h5-regular'>{x.SecondaryLabel}</div>
                                                    </div>}
                                                />
                                            </div>)
                                        }
                                    </div>
                                </RadioGroup>
                            </div>
                            <div className='h3-medium color-primary-accent '>Select Groups</div>
                            <Autocomplete
                                multiple
                                size='small'
                                className={"mt-1 w-p-98"}
                                options={Groups}
                                getOptionLabel={option => option.Name}
                                value={Groups.filter(x => selectedGroupIds.includes(x.ID)) || []}
                                onChange={onGroupChange}
                                getOptionDisabled={(option) => (!!selectedGroupIds?.length && selectedGroupIds?.findIndex(x => x === option.ID) !== -1) || option.ContactsCount >= MaxGroupContactCount || disableOptions(option)}
                                filterSelectedOptions
                                renderOption={(defaultprops, option, { selected }) => (
                                    <li {...defaultprops} className={`${defaultprops.className} c-pointer h4-regular font-bold p-2 d-flex justify-between ${selected ? 'Mui-focused' : ''}`}>
                                        <div>
                                            {option.Name}
                                        </div>
                                        <div className="badge-label">
                                            {option.ContactsCount}
                                        </div>
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            avatar={<Avatar> {option.ContactsCount}</Avatar>}
                                            classes={{
                                                colorPrimary: "custom-color-chip-2",
                                                avatarColorPrimary: "custom-chip-avatar",
                                            }}
                                            color='primary'
                                            label={option.Name}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Groups"
                                        variant='outlined'
                                        placeholder=""
                                        inputProps={{ ...params.inputProps, autoComplete: 'no' }}
                                    />
                                )}
                            />
                            <div className='text-between-line p-1 w-p-80 mx-auto'>or</div>

                            <div className='d-flex justify-between w-p-98'>
                                <TextField
                                    placeholder="Create a new Group"
                                    name="NewGroupName"
                                    variant="outlined"
                                    size="small"
                                    value={NewGroupName}
                                    className={isLoading ? "w-p-85" : "w-p-88"}
                                    onChange={(e) =>
                                        editGroup(e.target.name, e.target.value)}
                                />
                                <ButtonComponent
                                    ButtonLabel={'+ Create'}
                                    Variant={(NewGroupName && !errors.NewGroupName) ? 'secondary-btn-small' : 'disabled-btn-small'}
                                    OnClick={() => createGroup()}
                                    Loading={isLoading} />
                            </div>
                            <div className='color-primary-danger h5-regular ml-2'>{errors?.NewGroupName}</div>

                            <div className='my-1 d-flex justify-between bg-color-report-background br-2 align-center'>
                                <div className='h5-regular color-primary-accent d-flex align-center'>
                                    <Checkbox
                                        checked={checked}
                                        onChange={(e) => { setChecked(e.target.checked); props.UpdateIncludeIsGroupCheck(e.target.checked) }}
                                        classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                    />
                                    <span>Don’t put them in a group right now</span>
                                </div>
                                <span className='h5-regular color-menu-list-font-subnav mr-2'>You can group them later from Contacts</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='bg-color-con-hist-outbound-msg-bg br-2 w-p-100 d-flex align-center h5-regular color-menu-list-font-subnav my-2 py-1'>
                    {/* <div className='w-4 h-4 br-50 mx-2 bg-color-primary-light'></div> */}
                    <div className='ml-1'>
                        When you click Import:
                        <span className='color-primary-accent h4-medium mx-1'>
                            {(props.ExistingContacts?.length ?? 0) + (props.NewContacts?.length ?? 0)} contacts
                        </span>

                        {(props.ExistingContacts?.length || props.NewContacts?.length) && (
                            <>
                                (
                                {props.NewContacts?.length ? `${props.NewContacts.length} new` : ""}
                                {props.NewContacts?.length && props.ExistingContacts?.length ? ", " : ""}
                                {props.ExistingContacts?.length ? `${props.ExistingContacts.length} existing` : ""}
                                )
                            </>
                        )}

                        {" "}· existing ones will{" "}
                        <span className='color-primary-accent h4-medium mx-1'>{existingContactActionData.find(x => x.Value === selectExistingContactAction)?.PrimaryLabel}</span>

                        {!!selectedGroupIds?.length && (
                            <>
                                {" "}· added to{" "}
                                <span className='color-primary-accent h4-medium mx-1'>
                                    {Groups
                                        .filter(x => selectedGroupIds?.includes(x.ID))
                                        .map(x => x.Name)
                                        .join(", ")}
                                </span>
                            </>
                        )}
                        .
                    </div>
                </div>
                <div className='my-1 border-primary-divider p-1 br-1 h5-regular color-menu-list-font-subnav bg-color-report-background'>
                    By clicking Import, you confirm these contacts have given their consent to receive SMS messages from your organization.
                </div>
            </div>
        </div>
    )
}
export default ExcelContactToGroup;